import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import AdminLogin from './AdminLogin';
import LoadingSpinner from '@medical/shared/components/LoadingSpinner';
import ForcePasswordChange from '@medical/shared/components/ForcePasswordChange';
import { verifyStoredSession } from '@medical/shared/utils/verifyStoredSession';

const AdminProtectedRoute = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    checkAdminAuthStatus();

    // Listen for storage changes (for logout from other tabs/windows)
    const handleStorageChange = (e) => {
      if (e.key === 'adminToken' || e.key === null) {
        checkAdminAuthStatus();
      }
    };
    window.addEventListener('storage', handleStorageChange);

    // Also listen for custom logout event
    const handleLogout = () => {
      setIsAuthenticated(false);
      setUser(null);
    };
    window.addEventListener('adminLogout', handleLogout);

    // Listen for admin login event
    const handleAdminLogin = () => {
      checkAdminAuthStatus();
    };
    window.addEventListener('adminLogin', handleAdminLogin);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('adminLogout', handleLogout);
      window.removeEventListener('adminLogin', handleAdminLogin);
    };
  }, []);

  const checkAdminAuthStatus = async () => {
    try {
      const verified = await verifyStoredSession({
        tokenKey: 'adminToken',
        refreshKey: 'adminRefreshToken',
        userKey: 'adminUser',
        role: 'admin'
      });
      setUser(verified);
      setIsAuthenticated(Boolean(verified));
      if (!verified && window.location.pathname !== '/admin') {
        navigate('/admin', { replace: true });
      }
    } catch (error) {
      console.error('Admin auth check failed:', error);
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    return <AdminLogin onLoginSuccess={(user) => {
      setUser(user);
      setIsAuthenticated(true);
    }} />;
  }

  if (user?.mustChangePassword) {
    return (
      <ForcePasswordChange
        accountLabel={user.adminId || user.email}
        onSignOut={() => {
          ['adminToken', 'adminRefreshToken', 'adminUser'].forEach((key) => localStorage.removeItem(key));
          delete axios.defaults.headers.common['Authorization'];
          setUser(null);
          setIsAuthenticated(false);
        }}
        onChanged={() => {
          const next = { ...user, mustChangePassword: false };
          setUser(next);
          localStorage.setItem('adminUser', JSON.stringify(next));
        }}
      />
    );
  }

  return children;
};

export default AdminProtectedRoute;

